import React, { useState, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ExportPackConfig, ExportTableRequest } from "object-models/ExportPackModel";
import { generateExportPack } from "utilities/exportPackGenerator";
import {
  _exportProjectRegister,
  _exportScenarioView,
  _exportInputDataPack,
} from "middlewares/ExportApi/ExportApi";
import { useExportPack } from "context/exportPack.context";
import { useAppState } from "context/appState.context";
import { _getScenarioByLocationID } from "middlewares/DashboardApi/scenarioView";
import {
  ORANGE,
  ORANGE_GRADIENT,
  colors,
  cardSx,
  sectionHeaderSx,
  sectionLabelSx,
  formatBtnSx,
  textSm,
  textXs,
} from "./ExportPackDrawer.styles";

/* ── Custom square checkbox ── */
const CustomCheck: React.FC<{ checked: boolean; size?: number; label?: string }> = ({ checked, size = 20, label }) => (
  <Box
    sx={{
      position: "relative",
      width: size,
      height: size,
      flexShrink: 0,
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      readOnly
      tabIndex={0}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        opacity: 0,
        margin: 0,
        cursor: "pointer",
        pointerEvents: "none",
      }}
    />
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid",
        borderColor: checked ? colors.orange : colors.gray300,
        bgcolor: checked ? colors.orange : colors.white,
        transition: "all 150ms",
        pointerEvents: "none",
      }}
    >
      {checked && <CheckIcon sx={{ fontSize: size * 0.7, color: colors.white }} />}
    </Box>
  </Box>
);

function buildSummaryItems(config: ExportPackConfig, actualMeasureCount: number) {
  const items: { icon: React.ReactNode; label: string }[] = [];
  if (config.includeInputDataPack) items.push({ icon: <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />, label: "Input Data Pack" });
  if (config.includeProjectRegister) items.push({ icon: <ViewListOutlinedIcon sx={{ fontSize: 13 }} />, label: `Project Register (${actualMeasureCount} measures)` });
  if (config.includeScenarioView) items.push({ icon: <CallSplitOutlinedIcon sx={{ fontSize: 13 }} />, label: "Decarbonization Plan" });
  const selectedChartCount = Object.values(config.charts).filter(Boolean).length;
  if (selectedChartCount > 0) items.push({ icon: <BarChartOutlinedIcon sx={{ fontSize: 13 }} />, label: `${selectedChartCount} Chart(s)` });
  return items;
}

function buildFormatPills(config: ExportPackConfig, someChartsSelected: boolean) {
  const pills: { label: string; orange: boolean }[] = [];
  if (config.includeInputDataPack || config.includeProjectRegister || config.includeScenarioView) {
    pills.push({ label: config.tableFormat.toUpperCase(), orange: true });
  }
  if (someChartsSelected) pills.push({ label: config.chartFormat.toUpperCase(), orange: true });
  pills.push({ label: ".zip", orange: false });
  return pills;
}

function buildExportRequest(params: {
  locationId: number;
  dashboardSection: string;
  ghgScope: string[];
  startYear: number | null;
  endYear: number | null;
  scenario: string | null;
  businessUnit: string | null;
  tableFormat: 'xlsx' | 'csv';
  locationName: string;
}): ExportTableRequest {
  return {
    locationId: params.locationId,
    ...(params.dashboardSection ? { dashboardSection: params.dashboardSection } : {}),
    ...(params.ghgScope && params.ghgScope.length > 0 ? { ghgScope: params.ghgScope } : {}),
    startYear: params.startYear,
    endYear: params.endYear,
    scenario: params.scenario,
    businessUnit: params.businessUnit,
    format: params.tableFormat,
    locationName: params.locationName,
  };
}

const ExportPackDrawer: React.FC = () => {
  const {
    isDrawerOpen: open,
    closeDrawer: onClose,
    locationId,
    locationName,
    dashboardSection,
    ghgScope,
    startYear,
    endYear,
    scenario,
    businessUnit,
    measureCount,
    scenarioOptions,
  } = useExportPack();

  const { currency, setNotificationConfig } = useAppState();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const [config, setConfig] = useState<ExportPackConfig>({
    includeInputDataPack: true,
    includeProjectRegister: false,
    includeScenarioView: false,
    scenarioMode: "all",
    selectedScenarios: [],
    charts: {
      macCurve: false,
      timelineFinancialMetrics: false,
      measureImpactByScope: false,
      glidepath: false,
    },
    tableFormat: "xlsx",
    chartFormat: "png",
  });
  const [generating, setGenerating] = useState(false);
  const [loadedScenarioOptions, setLoadedScenarioOptions] = useState<string[]>([]);
  const [loadedMeasureCount, setLoadedMeasureCount] = useState(0);

  // Fetch scenario options and measure count when drawer opens
  React.useEffect(() => {
    if (open && locationId > 0) {
      _getScenarioByLocationID(locationId).then((scenarios) => {
        const sections = [...new Set(scenarios.map((s) => s.scenarioSection).filter(Boolean))] as string[];
        setLoadedScenarioOptions(sections);
        const uniqueMeasures = new Set(scenarios.map((s) => s.reductionMeasureId).filter(Boolean));
        setLoadedMeasureCount(uniqueMeasures.size);
      }).catch(() => {
        setLoadedScenarioOptions([]);
        setLoadedMeasureCount(0);
      });
    }
  }, [open, locationId]);

  const availableScenarios = loadedScenarioOptions.length > 0 ? loadedScenarioOptions : scenarioOptions;
  const actualMeasureCount = loadedMeasureCount > 0 ? loadedMeasureCount : measureCount;

  const handleRemoveScenario = (scenario: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedScenarios: prev.selectedScenarios.filter((s) => s !== scenario),
    }));
  };

  const handleChartToggle = (key: keyof ExportPackConfig["charts"]) => {
    setConfig((prev) => ({
      ...prev,
      charts: { ...prev.charts, [key]: !prev.charts[key] },
    }));
  };

  const allChartsSelected =
    config.charts.macCurve &&
    config.charts.timelineFinancialMetrics &&
    config.charts.measureImpactByScope &&
    config.charts.glidepath;
  const someChartsSelected = Object.values(config.charts).some(Boolean);
  const chartsChecked = allChartsSelected || someChartsSelected;

  const handleChartsToggle = () => {
    const allOff = { macCurve: false, timelineFinancialMetrics: false, measureImpactByScope: false, glidepath: false };
    const allOn = { macCurve: true, timelineFinancialMetrics: true, measureImpactByScope: true, glidepath: true };
    setConfig((prev) => ({ ...prev, charts: someChartsSelected ? allOff : allOn }));
  };

  const hasAnySelection =
    config.includeInputDataPack ||
    config.includeScenarioView ||
    (config.includeScenarioView && config.includeProjectRegister) ||
    (config.includeScenarioView && someChartsSelected);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const request: ExportTableRequest = buildExportRequest({
        locationId, dashboardSection, ghgScope, startYear, endYear, scenario, businessUnit, tableFormat: config.tableFormat, locationName,
      });

      const outcome = await generateExportPack(
        config,
        request,
        {
          exportProjectRegister: _exportProjectRegister,
          exportScenarioView: _exportScenarioView,
          exportInputDataPack: _exportInputDataPack,
        },
        locationName,
        availableScenarios,
        chartContainerRef.current
          ? {
              container: chartContainerRef.current,
              currency,
              locationId,
              ghgScope,
              startYear,
              endYear,
            }
          : undefined
      );

      if (outcome.chartErrors.length > 0) {
        const allChartsFailed = outcome.chartCount === 0;
        setNotificationConfig({
          open: true,
          severity: allChartsFailed ? "error" : "warning",
          message: allChartsFailed
            ? "Export Pack generated without charts. See Charts/_errors.txt in the zip for details."
            : `Export Pack generated, but ${outcome.chartErrors.length} chart(s) could not be included. See Charts/_errors.txt in the zip for details.`,
        });
      } else {
        setNotificationConfig({
          open: true,
          severity: "success",
          message: "Export Pack generated successfully.",
        });
      }
      onClose();
    } catch (err) {
      console.error("Export pack generation failed:", err);
      setNotificationConfig({
        open: true,
        severity: "error",
        message: "Export Pack generation failed. Please try again.",
      });
    } finally {
      setGenerating(false);
    }
  };

  /* helper: summary items */
  const summaryItems = buildSummaryItems(config, actualMeasureCount);

  /* helper: format pills */
  const formatPills = buildFormatPills(config, someChartsSelected);

  const chartItems: { key: keyof ExportPackConfig["charts"]; label: string; icon: React.ReactNode }[] = [
    { key: "macCurve", label: "MAC Curve", icon: <TrendingDownOutlinedIcon sx={{ fontSize: 13 }} /> },
    { key: "timelineFinancialMetrics", label: "Timeline & Financial Metrics", icon: <TimelineOutlinedIcon sx={{ fontSize: 13 }} /> },
    { key: "measureImpactByScope", label: "Measure Impact by Scope", icon: <LayersOutlinedIcon sx={{ fontSize: 13 }} /> },
    { key: "glidepath", label: "Glidepath", icon: <ShowChartOutlinedIcon sx={{ fontSize: 13 }} /> },
  ];

  return (
    <>
    {/* Chart rendering container — positioned far off-screen so it stays
        visible to layout/paint (required by html-to-image) but never visible
        to the user. opacity:0 / display:none / zIndex:-1 break capture. */}
    <div
      ref={chartContainerRef}
      aria-hidden
      style={{
        position: 'fixed',
        left: '-100000px',
        top: 0,
        width: '1300px',
        minHeight: '600px',
        background: '#ffffff',
        pointerEvents: 'none',
      }}
    />
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          top: 0,
          height: '100vh',
          bottom: "auto",
          bgcolor: colors.gray50,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* ─── Header ─── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 2,
          bgcolor: colors.white,
          borderBottom: `1px solid ${colors.gray200}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            background: ORANGE_GRADIENT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 18, color: colors.white }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: colors.gray800 }}>
            Generate Export Pack
          </Typography>
          <Typography sx={{ ...textXs, color: colors.gray400 }}>
            Configure your bundled .zip deliverable
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            ml: "auto",
            p: 0.75,
            borderRadius: "8px",
            border: "none",
            bgcolor: "transparent",
            color: colors.gray400,
            cursor: "pointer",
            display: "flex",
            "&:hover": { bgcolor: colors.gray100, color: colors.gray700 },
            transition: "all 150ms",
          }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {/* ─── Scrollable content ─── */}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: 2, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>

        {/* Input Data Pack */}
        <Box sx={cardSx}>
          <Box
            component="label"
            onClick={() => setConfig((prev) => ({ ...prev, includeInputDataPack: !prev.includeInputDataPack }))}
            sx={sectionHeaderSx(config.includeInputDataPack)}
          >
            <CustomCheck checked={config.includeInputDataPack} />
            <Box sx={sectionLabelSx(config.includeInputDataPack)}>
              <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={textSm}>Input Data Pack</Typography>
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${colors.gray100}` }}>
            <Typography sx={{ ...textXs, color: colors.gray400, display: "flex", alignItems: "center", gap: 0.75 }}>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: colors.orangeLight }} />
              Full input dataset included
            </Typography>
          </Box>
        </Box>

        {/* Decarbonization Plan */}
        <Box sx={cardSx}>
          <Box
            component="label"
            onClick={() => setConfig((prev) => ({
              ...prev,
              includeScenarioView: !prev.includeScenarioView,
              ...(!prev.includeScenarioView ? {} : {
                includeProjectRegister: false,
                charts: { macCurve: false, timelineFinancialMetrics: false, measureImpactByScope: false, glidepath: false },
              }),
            }))}
            sx={sectionHeaderSx(config.includeScenarioView)}
          >
            <CustomCheck checked={config.includeScenarioView} />
            <Box sx={sectionLabelSx(config.includeScenarioView)}>
              <CallSplitOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={textSm}>Decarbonization Plan</Typography>
            </Box>
          </Box>
          {config.includeScenarioView && (
            <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.gray100}`, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <RadioGroup
                row
                value={config.scenarioMode}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  scenarioMode: e.target.value as "all" | "selected",
                  selectedScenarios: e.target.value === "all" ? [] : prev.selectedScenarios,
                }))}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio sx={{ color: colors.gray300, "&.Mui-checked": { color: colors.orange }, p: 0.5, mr: 0.5 }} size="small" />}
                  label={<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: colors.gray700 }}>All plans</Typography>}
                  sx={{ mr: 3 }}
                />
                <FormControlLabel
                  value="selected"
                  control={<Radio sx={{ color: colors.gray300, "&.Mui-checked": { color: colors.orange }, p: 0.5, mr: 0.5 }} size="small" />}
                  label={<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: colors.gray700 }}>Selected plans</Typography>}
                />
              </RadioGroup>
              {config.scenarioMode === "selected" && (
                <>
                  <Select
                    multiple
                    displayEmpty
                    value={config.selectedScenarios}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const val = e.target.value;
                      setConfig((prev) => ({
                        ...prev,
                        selectedScenarios: typeof val === "string" ? val.split(",") : val,
                      }));
                    }}
                    IconComponent={KeyboardArrowDownIcon}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ fontSize: "0.875rem", color: colors.gray400 }}>Select plans...</Typography>;
                      }
                      if (selected.length === availableScenarios.length) {
                        return <Typography sx={{ fontSize: "0.875rem", color: colors.gray700 }}>All plans</Typography>;
                      }
                      return <Typography sx={{ fontSize: "0.875rem", color: colors.gray700 }}>{selected.length} plan(s) selected</Typography>;
                    }}
                    sx={{
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      bgcolor: colors.white,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray200 },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.orangeLight },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.orange },
                      "& .MuiSelect-select": { py: 1.25 },
                    }}
                    size="small"
                    fullWidth
                  >
                    {availableScenarios.map((opt) => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: "0.875rem" }}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {config.selectedScenarios.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {config.selectedScenarios.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          onDelete={() => handleRemoveScenario(s)}
                          size="small"
                          sx={{
                            bgcolor: colors.orangeBg,
                            color: colors.orangeText,
                            borderColor: colors.orangeLight,
                            border: "1px solid",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            "& .MuiChip-deleteIcon": {
                              color: colors.orangeText,
                              fontSize: 16,
                              "&:hover": { color: "#9A3412" },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Project Register */}
        <Box sx={{ ...cardSx, opacity: config.includeScenarioView ? 1 : 0.5, transition: "opacity 150ms" }}>
          <Box
            component="label"
            onClick={() => config.includeScenarioView && setConfig((prev) => ({ ...prev, includeProjectRegister: !prev.includeProjectRegister }))}
            sx={{ ...sectionHeaderSx(config.includeProjectRegister), cursor: config.includeScenarioView ? "pointer" : "not-allowed" }}
          >
            <CustomCheck checked={config.includeProjectRegister} />
            <Box sx={sectionLabelSx(config.includeProjectRegister)}>
              <ViewListOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={textSm}>Project Register</Typography>
            </Box>
          </Box>
          {config.includeProjectRegister && (
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${colors.gray100}` }}>
              <Typography sx={{ ...textXs, color: colors.gray500 }}>
                Includes all <Box component="span" sx={{ fontWeight: 600, color: colors.gray700 }}>{actualMeasureCount}</Box> measures with full financial and emissions data.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Charts */}
        <Box sx={{ ...cardSx, opacity: config.includeScenarioView ? 1 : 0.5, transition: "opacity 150ms" }}>
          <Box
            component="label"
            onClick={() => config.includeScenarioView && handleChartsToggle()}
            sx={{ ...sectionHeaderSx(chartsChecked), cursor: config.includeScenarioView ? "pointer" : "not-allowed" }}
          >
            <CustomCheck checked={chartsChecked} />
            <Box sx={sectionLabelSx(chartsChecked)}>
              <BarChartOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={textSm}>Charts</Typography>
            </Box>
          </Box>

          {chartsChecked && (
            <Box sx={{ px: 2, pb: 2, pt: 1.5, borderTop: `1px solid ${colors.gray100}`, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Individual charts */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {chartItems.map((item) => (
                  <Box
                    key={item.key}
                    component="label"
                    onClick={() => handleChartToggle(item.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      userSelect: "none",
                      border: "1px solid",
                      borderColor: config.charts[item.key] ? colors.orangeLight : colors.gray100,
                      bgcolor: config.charts[item.key] ? colors.orangeBg : colors.gray50,
                      transition: "all 150ms",
                      "&:hover": { borderColor: config.charts[item.key] ? colors.orangeLight : colors.gray200 },
                    }}
                  >
                    <CustomCheck checked={config.charts[item.key]} size={16} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: config.charts[item.key] ? colors.orangeText : colors.gray400 }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ ...textXs, fontWeight: 500, color: config.charts[item.key] ? colors.orangeText : colors.gray600 }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Format Selection */}
        <Box sx={cardSx}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: colors.gray50 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <TableChartOutlinedIcon sx={{ fontSize: 14, color: colors.gray400 }} />
              <Typography sx={{ ...textXs, fontWeight: 600, color: colors.gray700 }}>Format Selection</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography sx={{ ...textXs, color: colors.gray400, mb: 1 }}>Tables</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {(["xlsx", "csv"] as const).map((fmt) => (
                    <Box
                      key={fmt}
                      component="button"
                      onClick={() => setConfig((prev) => ({ ...prev, tableFormat: fmt }))}
                      sx={formatBtnSx(config.tableFormat === fmt)}
                    >
                      {fmt === "xlsx" ? "Excel (.xlsx)" : "CSV"}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ ...textXs, color: colors.gray400, mb: 1 }}>Charts</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {(["png", "jpeg"] as const).map((fmt) => (
                    <Box
                      key={fmt}
                      component="button"
                      onClick={() => setConfig((prev) => ({ ...prev, chartFormat: fmt }))}
                      sx={formatBtnSx(config.chartFormat === fmt)}
                    >
                      {fmt.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Export Summary */}
        <Box sx={{ borderRadius: "12px", border: "1px solid #F5D9B0", overflow: "hidden", flexShrink: 0 }}>
          {/* Summary header */}
          <Box sx={{ px: 2, py: 1.25, bgcolor: colors.orangeBg, display: "flex", alignItems: "center", gap: 1 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 13, color: ORANGE }} />
            <Typography sx={{ ...textXs, fontWeight: 600, color: colors.orangeDeepText }}>Export Summary</Typography>
          </Box>
          {/* Summary body */}
          <Box sx={{ bgcolor: colors.white, px: 2, py: 1.5 }}>
            <Typography sx={{ ...textXs, color: colors.gray500, mb: 1 }}>Your export will include:</Typography>
            {summaryItems.map((item, idx) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  py: 0.75,
                  ...(idx > 0 ? { borderTop: `1px solid ${colors.gray50}` } : {}),
                }}
              >
                <Box sx={{ flexShrink: 0, mt: 0.25, color: "#FB923C", display: "flex" }}>{item.icon}</Box>
                <Typography sx={{ ...textXs, color: colors.gray700 }}>{item.label}</Typography>
              </Box>
            ))}
            {summaryItems.length === 0 && (
              <Typography sx={{ ...textXs, color: colors.gray400, py: 0.5 }}>No items selected</Typography>
            )}
            {/* Format footer */}
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${colors.gray100}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ ...textXs, color: colors.gray400 }}>Output format</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {formatPills.map((pill) => (
                  <Box
                    key={pill.label}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      border: "1px solid",
                      ...(pill.orange
                        ? { bgcolor: colors.orangeBg, color: colors.orangeDeep, borderColor: "#FFEDD5" }
                        : { bgcolor: colors.gray100, color: colors.gray500, borderColor: colors.gray200 }),
                    }}
                  >
                    {pill.label}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bottom spacer */}
        <Box sx={{ height: 16 }} />
      </Box>

      {/* ─── Footer ─── */}
      <Box
        sx={{
          px: 2,
          py: 2,
          bgcolor: colors.white,
          borderTop: `1px solid ${colors.gray200}`,
          flexShrink: 0,
        }}
      >
        <Button
          fullWidth
          onClick={handleGenerate}
          disabled={generating || !hasAnySelection}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            py: 1.5,
            borderRadius: "12px",
            fontSize: "0.875rem",
            fontWeight: 600,
            textTransform: "none",
            color: colors.white,
            background: ORANGE_GRADIENT,
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
            "&:hover": {
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
              background: ORANGE_GRADIENT,
            },
            "&:active": { transform: "scale(0.98)" },
            "&.Mui-disabled": {
              background: `linear-gradient(135deg, ${colors.orangeLight} 0%, ${colors.orange} 100%)`,
              color: "rgba(255,255,255,0.7)",
            },
            transition: "all 150ms",
          }}
        >
          {generating ? (
            <CircularProgress size={16} sx={{ color: colors.white }} />
          ) : (
            <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
          )}
          {generating ? "Generating..." : "Generate Export Pack"}
        </Button>
      </Box>
    </Drawer>
    </>
  );
};

export default ExportPackDrawer;
